import React, { Component, ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';
import { logError } from '@/lib/errorTracking';
import { readStoredLanguage, useOptionalLanguage } from '@/hooks/useLanguage';
import { translations, type TranslationKey } from '@/lib/translations';

/** True when the error is a Vite/Rollup lazy-chunk fetch failure. */
function isChunkError(error: Error): boolean {
  return (
    error.message?.includes('Failed to fetch dynamically imported module') ||
    error.message?.includes('is not a valid JavaScript MIME type') ||
    error.name === 'ChunkLoadError'
  );
}

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
  homeHref?: string;
}

/** Short, non-sensitive reference a user can quote to support; the full error is in logError's payload. */
function errorReferenceId(error: Error): string {
  let hash = 0;
  const text = `${error.name}:${error.message}`;
  for (let i = 0; i < text.length; i++) {
    hash = (hash * 31 + text.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(36).slice(0, 6).toUpperCase();
}

function ErrorFallback({ error, resetErrorBoundary, homeHref = '/' }: ErrorFallbackProps) {
  // The outer App boundary is above LanguageProvider. Shipped copy needs no
  // database/context that may itself have failed while rendering the app.
  const language = useOptionalLanguage()?.language ?? readStoredLanguage();
  const t = (key: TranslationKey) => translations[language][key];
  if (isChunkError(error)) {
    return (
            <div lang={language} dir={language === 'ar' ? 'rtl' : 'ltr'} className="min-h-dvh bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <RefreshCw className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle>{t('errorBoundaryUpdateTitle')}</CardTitle>
                <CardDescription>
                  {t('errorBoundaryUpdateDescription')}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Button onClick={() => window.location.reload()} className="w-full">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('errorBoundaryRefresh')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
        <div lang={language} dir={language === 'ar' ? 'rtl' : 'ltr'} className="min-h-dvh bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <div>
              <CardTitle>{t('errorBoundaryTitle')}</CardTitle>
              <CardDescription>
                {t('errorBoundaryDescription')}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">
            {t('errorBoundaryReferenceLabel')}: <span className="font-mono">{errorReferenceId(error)}</span> — {t('errorBoundaryReferenceHelp')}
          </p>

          {import.meta.env.DEV && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm font-mono text-muted-foreground">
                {error.message}
              </p>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={resetErrorBoundary}
              className="flex-1"
              variant="default"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('errorBoundaryRetry')}
            </Button>
            <Button
              onClick={() => window.location.href = homeHref}
              variant="outline"
              className="flex-1"
            >
              <Home className="h-4 w-4 mr-2" />
              {t('errorBoundaryHome')}
            </Button>
          </div>

          {import.meta.env.DEV && error.stack && (
            <details className="mt-4">
              <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                {t('errorBoundaryStack')}
              </summary>
              <pre className="mt-2 text-xs bg-muted p-3 rounded overflow-auto max-h-64">
                {error.stack}
              </pre>
            </details>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface ErrorBoundaryWrapperProps {
  children: ReactNode;
  componentName?: string;
  /** Override the "Go Home" button destination (e.g. '/uat' for UAT pages) */
  homeHref?: string;
}

export function ErrorBoundaryWrapper({
  children,
  componentName = 'App',
  homeHref = '/',
}: ErrorBoundaryWrapperProps) {
  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    // Log to our error tracking system
    logError(error, {
      componentName,
      metadata: { componentStack: errorInfo.componentStack },
      severity: 'critical',
    });
  };

  const FallbackWithHomeHref = (props: ErrorFallbackProps) => (
    <ErrorFallback {...props} homeHref={homeHref} />
  );

  return (
    <ErrorBoundary
      FallbackComponent={FallbackWithHomeHref}
      onError={handleError}
      onReset={() => {
        window.location.reload();
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
